import React, { useEffect, useState } from "react";
import { CustomIcon } from "../shared/CustomIcon";
import { apiSlotHistory, apiMemberHistory } from "./api";

// Iter-54 (E): Slot History + Member History lookup modals used by the
// tree node detail panel. Super Admin only.
export default function NodeHistoryModal({ kind, node, member, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    const p = kind === "slot"
      ? apiSlotHistory(node?.nodeId)
      : apiMemberHistory(member?.id);
    p
      .then(r => { if (cancelled) return; setItems(r?.items || []); setMeta(r || null); })
      .catch(e => { if (cancelled) return; setError(e?.message || "Failed to load history."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kind, node?.nodeId, member?.id]);

  const title = kind === "slot"
    ? `Slot History · ${node?.slotRef || "—"}`
    : `Member History · ${member ? `${member.firstName || ""} ${member.lastName || ""}`.trim() : "—"}`;

  return (
    <div
      data-testid="node-history-modal"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 320, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)", borderRadius: 14,
          width: "min(560px, 100%)", maxHeight: "85vh", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}
      >
        <div style={{ padding: "16px 18px", borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", display: "flex", alignItems: "center", gap: 10 }}>
          <CustomIcon name={kind === "slot" ? "home" : "person"} size={16} />
          <div style={{ flex: 1, fontFamily: "'Cinzel',serif", fontSize: 15, color: "var(--kc-accent)", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700 }}>
            {title}
          </div>
          <button data-testid="node-history-close" onClick={onClose} aria-label="Close" style={{ background: "transparent", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", color: "var(--kc-accent)", borderRadius: 999, width: 30, height: 30, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>
          {loading && <div style={{ color: "var(--kc-sub)", fontSize: 13 }}>Loading…</div>}
          {!loading && error && <div style={{ color: "#ff6b6b", fontSize: 13 }} data-testid="node-history-error">{error}</div>}
          {!loading && !error && items.length === 0 && (
            <div data-testid="node-history-empty" style={{ color: "var(--kc-sub)", fontSize: 13 }}>
              {kind === "slot" ? "No prior occupants for this slot." : "No slot occupancy history for this member."}
            </div>
          )}
          {!loading && !error && items.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {items.map((e, i) => (
                <div
                  key={`${e.memberId || e.nodeId || i}-${e.dateAssigned || i}`}
                  data-testid={`node-history-row-${i}`}
                  style={{ padding: "8px 10px", background: "#111", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8 }}
                >
                  <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                    {kind === "slot" ? (
                      <>
                        <strong style={{ color: "var(--kc-text)", fontSize: 13 }}>{e.memberName || "—"}</strong>
                        {e.memberNumber && <span style={{ color: "var(--kc-accent)", fontSize: 11 }}>#{e.memberNumber}</span>}
                      </>
                    ) : (
                      <>
                        <strong style={{ color: "var(--kc-text)", fontSize: 13 }}>{e.slotRef || "—"}</strong>
                        {e.levelName && <span style={{ color: "var(--kc-sub)", fontSize: 11 }}>{e.levelName}</span>}
                      </>
                    )}
                    <span style={{ marginLeft: "auto", color: "var(--kc-sub)", fontSize: 11 }}>
                      {(e.dateAssigned || "").slice(0, 10)} → {(e.dateDeparted || "current").slice(0, 10)}
                    </span>
                  </div>
                  {e.departureReason && (
                    <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 4 }}>
                      Departure: <span style={{ color: "#f59e0b" }}>{e.departureReason}</span>
                      {e.departureNotes ? ` — ${e.departureNotes}` : ""}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {meta && (
          <div style={{ padding: "10px 16px", borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", fontSize: 11, color: "var(--kc-meta)" }}>
            {items.length} record{items.length === 1 ? "" : "s"} · Super Admin view
          </div>
        )}
      </div>
    </div>
  );
}
